import { useRef, useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper as SwiperClass } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Virtual } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/virtual';

import styles from './DoubleView.module.css';
import { useOutsideClick } from '../../../../hooks/use-outside-click';
import { Icon } from '../../../../components/icon/Icon';
import { DoubleViewContext } from './Doubleview.context';

import { IssueContext } from '../../Issue.context';
import { ReplicaContext } from '../Replica.context';
import { AppContext } from '../../../../App.context';
import { useKeyPressShortcut } from '../../../../hooks/use-shortcut';
import { NavigationBar } from '../../../../components/navigation-bar/NavigationBar';
import { Page } from '../../../../components/article/Replica';
import { Hotspot } from '../../../../components/article/Replica.context';

import { Menu } from '../../menu/Menu';
import { Button } from '../../menu/Button';
import { NavbarIconLogo, fixInvalidInPages } from '../../../../constants';
import { useBackUrl } from '../../../../App.hooks';
import config from '../../../../config';

export const DoubleView = (props: DoubleViewProps) => {
	const { issue } = props;

	const { isSidebarOpen } = useContext(ReplicaContext);
	const { currentArticle, onClickLogOut } = useContext(IssueContext);
	const [swiper, setSwiper] = useState<SwiperClass>();
	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [index, setIndex] = useState(0);
	const classNames = [styles['issue']];
	if (isSidebarOpen) {
		classNames.push(styles['sidebar-open']);
	}

	const pages: Array<any> = useMemo(() => {
		if (!issue) {
			return [];
		}

		if (!issue.articles) {
			return [];
		}

		return issue.articles
			.reduce((acc: Array<any>, article: any) => {
				const { replica } = article;
				if (!replica) {
					console.log(
						`THIS ARTICLE IS THE PROBLEM: ${article.id} \n`,
						article,
					);
					return acc;
				}
				for (const item of replica) {
					item.articleId = article.id;
					acc.push(item);
				}
				return acc;
			}, [])
			.sort((a: any, b: any) => a.index - b.index)
			.reduce(removeDuplicatePages, { pagesSet: new Set(), pages: [] })
			.pages.reduce((acc: Array<any>, page: any, index: number) => {
				if (index === 0) {
					acc.push([null, page]);
				} else if (acc[acc.length - 1].length === 1 && index > 1) {
					acc[acc.length - 1].push(page);
				} else {
					acc[acc.length] = [page];
				}

				return acc;
			}, []);
	}, [issue]);

	useEffect(() => {
		if (isLoaded) {
			return;
		}

		if (!currentArticle) {
			return;
		}

		for (let i = 0; i < pages.length; i++) {
			const doublePage = pages[i];
			const [leftPage, rightPage] = doublePage;
			if (leftPage && leftPage.articleId === currentArticle.id) {
				setIndex(i);
				break;
			} else if (rightPage && rightPage.articleId === currentArticle.id) {
				setIndex(i);
				break;
			}
		}
		setIsLoaded(true);
	}, [pages, isLoaded, currentArticle]);

	useTrackPageArticle({
		isLoaded,
		pages,
		index,
	});

	const onNextArticleClick = () => {
		if (index === null) {
			return;
		}
		if (index + 1 >= pages.length) {
			return;
		}
		setIndex(index + 1);
	};

	const onPrevArticleClick = () => {
		if (index === null) {
			return;
		}
		if (index === 0) {
			return;
		}
		setIndex(index - 1);
	};

	useEffect(() => {
		if (!swiper) {
			return;
		}

		if (swiper.destroyed) {
			return;
		}

		swiper.slideTo(index, 750);
	}, [swiper, index]);

	useKeyPressShortcut(['ArrowRight'], onNextArticleClick);
	useKeyPressShortcut(['ArrowLeft'], onPrevArticleClick);

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	return (
		<DoubleViewContext.Provider
			value={{
				swiper,
				setSwiper,
				index,
				setIndex,
				pages,
				onNextArticleClick,
				onPrevArticleClick,
				toggleMenu,
			}}
		>
			<div className={classNames.join(' ')}>
				<Topbar />
				<Sidebar />
				<Container />
			</div>
			<Menu isOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
				{config?.app?.menu?.externalUrl?.enabled ? (
					<Button.WebsiteButton
						url={config?.app?.menu?.externalUrl.url}
						title={config?.app?.menu?.externalUrl.title}
					/>
				) : (
					<></>
				)}
				<Button.Share />
				<Button.Library />
				<Button.Responsive />
				<Button.SinglePage />
				<Button.Links links={config?.app?.menu?.links} />

				{onClickLogOut ? (
					<Button.SignOut onClick={onClickLogOut} />
				) : (
					<></>
				)}
				<Button.About />
			</Menu>
		</DoubleViewContext.Provider>
	);
};

interface DoubleViewProps {
	issue: any;
	onChangeResponsive: Function;
}

function removeDuplicatePages(
	acc: { pagesSet: Set<string>; pages: Array<any> },
	page: any,
) {
	const { pagesSet, pages } = acc;
	const { name } = page;
	if (pagesSet.has(name)) {
		return acc;
	}

	pagesSet.add(name);
	pages.push(page);

	return {
		pagesSet,
		pages,
	};
}

/**
 * React hook that tracks what is the article of the page
 *
 */
function useTrackPageArticle(props: TrackPageArticleArgs) {
	const { setCurrentArticle, currentArticle, articlesMap } =
		useContext(IssueContext);
	const { pages, index, isLoaded } = props;
	useEffect(() => {
		if (!articlesMap) {
			return;
		}
		if (!pages.length) {
			return;
		}
		if (!isLoaded) {
			return;
		}
		const doublePage = pages[index];
		const [leftPage, rightPage] = doublePage;
		let nextArticle = null;
		if (leftPage) {
			nextArticle = articlesMap.get(leftPage.articleId);
		} else if (rightPage) {
			nextArticle = articlesMap.get(rightPage.articleId);
		}

		if (nextArticle && nextArticle.id !== currentArticle.id) {
			setCurrentArticle(clone(nextArticle));
		}
	}, [pages, articlesMap, index]);
}

interface TrackPageArticleArgs {
	isLoaded: boolean;
	pages: Array<any>;
	index: number;
}

function Topbar() {
	const { isSidebarOpen, setIsSidebarOpen } = useContext(ReplicaContext);

	return (
		<nav className={styles['topbar']}>
			<div>
				<BackButton />
				<button
					className="tooltip down"
					data-text={isSidebarOpen ? 'Close' : 'Open'}
					type="button"
					onClick={() => {
						setIsSidebarOpen(!isSidebarOpen);
					}}
				>
					<Icon
						id={'sidebar-btn'}
						name={'sidebar'}
						color={
							isSidebarOpen
								? 'var(--app-accent-color)'
								: '#c3c3c3'
						}
					/>
				</button>
			</div>
			<div className={styles['logo']}>
				<NavbarIconLogo />
			</div>
			<div>
				<MenuButton />
			</div>
		</nav>
	);
}

function BackButton() {
	const url = useBackUrl();
	const navigate = useNavigate();
	const { issue } = useContext(IssueContext);
	if (!issue) {
		return null;
	}

	return (
		<button
			id="back-button"
			className="tooltip down"
			data-text="Back"
			onClick={() => {
				navigate(url);
			}}
			type="button"
			title="back"
		>
			<Icon name={'arrow-left'} color={'#c3c3c3'} />
		</button>
	);
}

function MenuButton() {
	const { toggleMenu } = useContext(DoubleViewContext);
	return (
		<button
			onClick={() => {
				toggleMenu();
			}}
		>
			<Icon id={'menu-btn'} name={'more-horizontal'} color={'#c3c3c3'} />
		</button>
	);
}

function Sidebar() {
	const { isSidebarOpen, setIsSidebarOpen } = useContext(ReplicaContext);
	const ref = useRef(null);
	useOutsideClick(
		ref,
		() => {
			if (isSidebarOpen) {
				setIsSidebarOpen(false);
			}
		},
		'sidebar-btn',
	);
	return (
		<aside ref={ref} className={styles['sidebar']}>
			<span></span>
			<Toc />
		</aside>
	);
}

function Toc() {
	const { pages, index } = useContext(DoubleViewContext);
	return (
		<ul className={styles['toc']}>
			{pages.map((replicaPage, position) => {
				return (
					<TocItem
						key={`list-${position}`}
						thumbnails={replicaPage.reduce(
							(acc: Array<any>, thumbnail: any) => {
								if (thumbnail)
									acc.push(
										fixInvalidInPages(thumbnail.image),
									);
								return acc;
							},
							[],
						)}
						titles={replicaPage.reduce(
							(acc: Array<any>, title: any) => {
								if (title) acc.push(title.name);
								return acc;
							},
							[],
						)}
						isActive={position === index}
						index={position}
						pages={replicaPage.reduce(
							(acc: Array<any>, page: any) => {
								if (page) acc.push(page.page);
								return acc;
							},
							[],
						)}
					/>
				);
			})}
		</ul>
	);
}

function TocItem({ thumbnails, isActive, index, pages }: TocItemProps) {
	const { device } = useContext(AppContext);
	const { setIsSidebarOpen } = useContext(ReplicaContext);
	const { setIndex } = useContext(DoubleViewContext);
	const onClick = () => {
		setIndex(index);
		if (device?.type === 'mobile') {
			setIsSidebarOpen(false);
		}
	};
	const contentProtection = config?.app?.contentProtection;
	const description = pages
		.map((page: number, position: number) => {
			if (page) return position === pages.length - 1 ? page : page + '-';
		})
		.join('');
	const thumbnailMap = (thumbnail: string, index: number) => {
		if (thumbnail)
			if (contentProtection) {
				return (
					<img
						title={`Page: ${description}`}
						alt={description}
						src={thumbnail}
						key={`Thumbnail-${index}`}
						className="content-protection"
						onContextMenu={(e) => e.preventDefault()}
					/>
				);
			}
		return (
			<img
				title={`Page: ${description}`}
				alt={description}
				src={thumbnail}
				key={`Thumbnail-${index}`}
			/>
		);
	};

	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
		<li
			className={isActive ? styles['active'] : undefined}
			onClick={onClick}
		>
			<div>{thumbnails.map(thumbnailMap)}</div>
			<span>
				<small>{description}</small>
			</span>
		</li>
	);
}

interface TocItemProps {
	titles: any;
	thumbnails: any;
	isActive: boolean;
	index: number;
	pages: any;
}

function Container() {
	const { onNextArticleClick, index, onPrevArticleClick, pages } =
		useContext(DoubleViewContext);
	const classNames = [styles['content']];
	return (
		<div className={classNames.join(' ')}>
			<DoublePages />
			{pages.length > 1 ? (
				<NavigationBar
					classNames={[styles['navigation-bar']]}
					onNextClick={onNextArticleClick}
					onPrevClick={onPrevArticleClick}
					disable={{
						left: index === 0,
						right: index === pages.length - 1,
					}}
				/>
			) : null}
		</div>
	);
}

export const DoublePages = () => {
	const { index, pages, setIndex } = useContext(DoubleViewContext);
	const { setSwiper } = useContext(DoubleViewContext);
	const onClickHotspot = (hotspot: Hotspot) => {
		const { url } = hotspot;
		open(url);
	};
	return (
		<div className={styles['double-page']}>
			<Swiper
				modules={[Virtual]}
				slidesPerGroup={1}
				spaceBetween={30}
				direction="horizontal"
				updateOnWindowResize={true}
				shortSwipes={true}
				initialSlide={index}
				scrollbar={{
					draggable: false,
				}}
				onSwiper={(s: SwiperClass) => {
					setSwiper(s);
				}}
				onSlideChange={(s: SwiperClass) => {
					const { activeIndex } = s;
					setIndex(activeIndex);
				}}
				virtual
			>
				{pages.map((doublePage, index) => (
					<SwiperSlide key={index} virtualIndex={index}>
						<div className={styles['swiper-container']}>
							<DoublePage
								leftPage={doublePage[0]}
								rightPage={doublePage[1]}
								onClickHotspot={onClickHotspot}
								index={index}
							/>
						</div>
					</SwiperSlide>
				))}
			</Swiper>
		</div>
	);
};

const DoublePage = ({
	leftPage,
	rightPage,
	onClickHotspot,
	index,
}: PageProps) => {
	return (
		<div id="double-page-container" className={styles['container']}>
			<div>
				{leftPage ? (
					<Page
						name={leftPage.name}
						image={leftPage.image}
						page={index}
						hotspots={leftPage.hotspots || []}
						onClickHotspot={onClickHotspot}
					/>
				) : null}
			</div>
			<div>
				{rightPage ? (
					<Page
						name={rightPage.name}
						image={rightPage.image}
						page={index}
						hotspots={rightPage.hotspots || []}
						onClickHotspot={onClickHotspot}
					/>
				) : null}
			</div>
		</div>
	);
};

interface PageProps {
	leftPage: any;
	rightPage: any;
	onClickHotspot: any;
	index: number;
}

function clone(obj: any) {
	if (structuredClone) {
		return structuredClone(obj);
	}
	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}
